import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, FileText, Loader2, LogOut, Pencil, Plus, Save, Trash2, Upload, X } from "lucide-react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import api, { formatApiErrorDetail } from "@/lib/api";

const EMPTY = { title_en: "", title_it: "", desc_en: "", desc_it: "", tech: "", year: "", image: "", featured: false, attachments: [] };

const inputCls = "w-full border border-line bg-ink px-3 py-2.5 text-sm text-white placeholder-zinc-600 outline-none transition-colors duration-200 focus:border-gold";

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, []);

  const loadProjects = () => api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  useEffect(() => {
    if (authed) loadProjects();
  }, [authed]);

  const login = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/login", { email, password });
      setAuthed(true);
      toast.success("ACCESS GRANTED");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setAuthed(false);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadFiles = async (e, target) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd);
        if (target === "image") set("image", data.url);
        else setForm((prev) => ({ ...prev, attachments: [...prev.attachments, data] }));
      }
      toast.success("UPLOAD COMPLETE");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setUploading(false);
    e.target.value = "";
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title_en.trim() && !form.title_it.trim()) {
      toast.error("Title (EN or IT) is required");
      return;
    }
    setSaving(true);
    const payload = {
      title_en: form.title_en || form.title_it,
      title_it: form.title_it || form.title_en,
      desc_en: form.desc_en,
      desc_it: form.desc_it,
      tech: form.tech.split(",").map((s) => s.trim()).filter(Boolean),
      year: form.year,
      image: form.image,
      featured: form.featured,
      attachments: form.attachments,
      order: editingId ? (projects.find((p) => p.id === editingId)?.order ?? 99) : projects.length + 1,
    };
    try {
      if (editingId) await api.put(`/projects/${editingId}`, payload);
      else await api.post("/projects", payload);
      toast.success(editingId ? "PROJECT UPDATED" : "PROJECT PUBLISHED");
      setForm(EMPTY);
      setEditingId(null);
      loadProjects();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setSaving(false);
  };

  const edit = (p) => {
    setEditingId(p.id);
    setForm({
      title_en: p.title_en, title_it: p.title_it,
      desc_en: p.desc_en, desc_it: p.desc_it,
      tech: (p.tech || []).join(", "), year: p.year, image: p.image,
      featured: p.featured, attachments: p.attachments || [],
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title_en}"?`)) return;
    await api.delete(`/projects/${p.id}`).catch(() => {});
    toast.success("PROJECT DELETED");
    if (editingId === p.id) {
      setEditingId(null);
      setForm(EMPTY);
    }
    loadProjects();
  };

  if (authed === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink font-mono text-xs tracking-[0.3em] text-zinc-500">
        CHECKING SESSION<span className="ml-2 inline-block h-3 w-2 bg-gold blink" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink font-mono text-white" data-testid="admin-page">
      <header className="sticky top-0 z-50 border-b border-line bg-black/70 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-[1440px] items-center justify-between px-6 md:px-10">
          <Link to="/" data-testid="admin-back-home" className="flex items-center gap-3 text-xs tracking-[0.25em] text-zinc-400 transition-colors hover:text-gold">
            <ArrowLeft size={14} /> SITE
          </Link>
          <div className="flex items-center gap-3">
            <Logo size={26} />
            <span className="text-xs font-bold tracking-[0.3em] text-gold">ADMIN.CONSOLE</span>
          </div>
          <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-2 border border-line px-3 py-2 text-[11px] tracking-[0.2em] text-zinc-300 transition-colors hover:border-gold hover:text-gold">
            <LogOut size={13} /> EXIT
          </button>
        </div>
      </header>

      {!authed ? (
        <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center px-6">
          <form onSubmit={login} className="w-full max-w-sm border border-line bg-panel p-8" data-testid="admin-login-form">
            <div className="text-xs tracking-[0.3em] text-gold">AUTH // ADMIN ONLY</div>
            <div className="mt-8 space-y-5">
              <div>
                <label className="text-[10px] tracking-[0.3em] text-zinc-500">EMAIL</label>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" className={`mt-2 ${inputCls}`} placeholder="> _" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.3em] text-zinc-500">PASSWORD</label>
                <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" className={`mt-2 ${inputCls}`} placeholder="> _" />
              </div>
              <button type="submit" data-testid="admin-login-btn" className="w-full bg-gold py-3 text-xs font-bold tracking-[0.3em] text-black transition-transform duration-200 hover:-translate-y-0.5">
                LOGIN
              </button>
            </div>
          </form>
        </div>
      ) : (
        <main className="mx-auto grid max-w-[1440px] gap-8 px-6 py-10 md:px-10 lg:grid-cols-12">
          <form onSubmit={save} className="border border-line bg-panel p-6 md:p-8 lg:col-span-7" data-testid="admin-project-form">
            <div className="flex items-center justify-between">
              <div className="text-xs tracking-[0.3em] text-gold">{editingId ? "EDIT PROJECT" : "NEW PROJECT"}</div>
              {editingId && (
                <button type="button" onClick={() => { setEditingId(null); setForm(EMPTY); }} data-testid="admin-reset-btn" className="flex items-center gap-1.5 text-[11px] tracking-[0.2em] text-zinc-500 hover:text-white">
                  <X size={12} /> NEW
                </button>
              )}
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-[10px] tracking-[0.3em] text-zinc-500">TITLE (EN) *</label>
                <input value={form.title_en} onChange={(e) => set("title_en", e.target.value)} data-testid="admin-title-en" className={`mt-2 ${inputCls}`} />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.3em] text-zinc-500">TITLE (IT) *</label>
                <input value={form.title_it} onChange={(e) => set("title_it", e.target.value)} data-testid="admin-title-it" className={`mt-2 ${inputCls}`} />
              </div>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-[10px] tracking-[0.3em] text-zinc-500">DESCRIPTION (EN)</label>
                <textarea rows={5} value={form.desc_en} onChange={(e) => set("desc_en", e.target.value)} data-testid="admin-desc-en" className={`mt-2 ${inputCls} resize-none`} />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.3em] text-zinc-500">DESCRIZIONE (IT)</label>
                <textarea rows={5} value={form.desc_it} onChange={(e) => set("desc_it", e.target.value)} data-testid="admin-desc-it" className={`mt-2 ${inputCls} resize-none`} />
              </div>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-[10px] tracking-[0.3em] text-zinc-500">TECH (COMMA SEPARATED)</label>
                <input value={form.tech} onChange={(e) => set("tech", e.target.value)} data-testid="admin-tech" className={`mt-2 ${inputCls}`} placeholder="TIA Portal, S7-1500, PROFINET" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.3em] text-zinc-500">YEAR</label>
                <input value={form.year} onChange={(e) => set("year", e.target.value)} data-testid="admin-year" className={`mt-2 ${inputCls}`} placeholder="2025" />
              </div>
            </div>

            <div className="mt-4">
              <label className="text-[10px] tracking-[0.3em] text-zinc-500">IMAGE URL</label>
              <input value={form.image} onChange={(e) => set("image", e.target.value)} data-testid="admin-image-url" className={`mt-2 ${inputCls}`} placeholder="https://..." />
              <label data-testid="admin-image-upload" className="mt-3 inline-flex cursor-pointer items-center gap-2 border border-line px-4 py-2 text-[11px] tracking-[0.2em] text-zinc-300 transition-colors hover:border-gold hover:text-gold">
                <Upload size={13} /> UPLOAD IMAGE
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadFiles(e, "image")} />
              </label>
              {form.image && <img src={form.image.startsWith("http") ? form.image : `${process.env.REACT_APP_BACKEND_URL}${form.image}`} alt="preview" className="mt-3 h-28 w-full object-cover" data-testid="admin-image-preview" />}
            </div>

            <div className="mt-6">
              <label className="text-[10px] tracking-[0.3em] text-zinc-500">ATTACHMENTS (PDF, EXPORTS, ...)</label>
              <label data-testid="admin-attachment-upload" className="mt-2 flex cursor-pointer items-center justify-center gap-2 border border-dashed border-line px-4 py-4 text-[11px] tracking-[0.2em] text-zinc-400 transition-colors hover:border-gold hover:text-gold">
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                {uploading ? "UPLOADING ..." : "ADD FILES"}
                <input type="file" multiple className="hidden" onChange={(e) => uploadFiles(e, "attachments")} />
              </label>
              {form.attachments.length > 0 && (
                <div className="mt-3 space-y-2" data-testid="admin-attachment-list">
                  {form.attachments.map((a, i) => (
                    <div key={i} className="flex items-center justify-between border border-line bg-ink px-3 py-2 text-[11px] text-zinc-300">
                      <span className="flex items-center gap-2"><FileText size={12} className="text-gold" /> {a.name}</span>
                      <button type="button" onClick={() => set("attachments", form.attachments.filter((_, j) => j !== i))} data-testid={`admin-attachment-remove-${i}`} className="text-zinc-500 hover:text-red-400">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <label className="mt-6 flex items-center gap-3 text-[11px] tracking-[0.2em] text-zinc-300" data-testid="admin-featured-check">
              <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="h-4 w-4 accent-[#FFD700]" />
              FEATURED (SHOWS LARGER ON SITE)
            </label>

            <button type="submit" disabled={saving} data-testid="admin-save-btn" className="mt-8 flex w-full items-center justify-center gap-3 bg-gold py-3.5 text-xs font-bold tracking-[0.3em] text-black transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {editingId ? "SAVE CHANGES" : "PUBLISH PROJECT"}
            </button>
          </form>

          <div className="lg:col-span-5">
            <div className="border border-line bg-panel">
              <div className="border-b border-line px-5 py-4 text-xs tracking-[0.3em] text-gold">PROJECTS.DB // {projects.length}</div>
              <div className="divide-y divide-white/5" data-testid="admin-project-list">
                {projects.map((p) => (
                  <div key={p.id} className="flex items-center justify-between gap-3 px-5 py-4" data-testid="admin-project-row">
                    <div className="min-w-0">
                      <div className="truncate text-xs font-bold text-white">{p.title_en}</div>
                      <div className="mt-1 text-[10px] tracking-[0.15em] text-zinc-500">
                        {p.year || "----"} // {p.attachments?.length || 0} FILES {p.featured ? "// FEATURED" : ""}
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <button onClick={() => edit(p)} data-testid={`admin-edit-${p.id}`} className="border border-line p-2 text-zinc-400 transition-colors hover:border-gold hover:text-gold" aria-label="Edit">
                        <Pencil size={13} />
                      </button>
                      <button onClick={() => remove(p)} data-testid={`admin-delete-${p.id}`} className="border border-line p-2 text-zinc-400 transition-colors hover:border-red-500 hover:text-red-400" aria-label="Delete">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                ))}
                {projects.length === 0 && <div className="px-5 py-8 text-center text-[11px] tracking-[0.25em] text-zinc-600">EMPTY DB</div>}
              </div>
            </div>
          </div>
        </main>
      )}
    </div>
  );
}
